import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Search, BarChart, ExternalLink, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { authService } from '@/utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminMarketing = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/seo-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching SEO settings:', error);
      toast.error('Ошибка загрузки настроек');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/seo-settings`, settings, {
        headers: authService.getAuthHeader()
      });
      toast.success('Настройки сохранены');
      // Reload page to apply Google Analytics
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div className="text-center py-12" data-testid="loading">Загрузка...</div>;
  }

  return (
    <div data-testid="admin-marketing-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className="font-heading text-4xl mb-8 tracking-tight">Маркетинг и SEO</h1>

        {/* Google Analytics Section */}
        <div className="bg-background p-6 rounded-sm border border-border mb-6">
          <div className="flex items-center gap-3 mb-4">
            <BarChart className="w-6 h-6 text-accent" />
            <h2 className="font-heading text-2xl tracking-tight">Google Analytics</h2>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4">
            Введите ваш Google Analytics Measurement ID (например: G-XXXXXXXXXX)
          </p>

          <div className="space-y-4">
            <div>
              <Label>Google Analytics ID</Label>
              <Input
                value={settings?.google_analytics_id || ''}
                onChange={(e) => handleChange('google_analytics_id', e.target.value)}
                placeholder="G-XXXXXXXXXX"
                data-testid="ga-id-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Получите ID на: <a href="https://analytics.google.com/" target="_blank" rel="noopener noreferrer" className="text-accent underline">analytics.google.com</a>
              </p>
            </div>

            {settings?.google_analytics_id && (
              <div className="p-4 bg-muted rounded">
                <p className="text-sm mb-2">✓ Google Analytics подключён</p>
                <a 
                  href="https://analytics.google.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-accent hover:underline inline-flex items-center gap-1"
                >
                  Открыть Google Analytics <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Google Search Console Section */}
        <div className="bg-background p-6 rounded-sm border border-border mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Search className="w-6 h-6 text-accent" />
            <h2 className="font-heading text-2xl tracking-tight">Google Search Console</h2>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4">
            Meta-тег для подтверждения сайта в Google Search Console
          </p>

          <div className="space-y-4">
            <div>
              <Label>Verification Code</Label>
              <Input
                value={settings?.google_search_console_id || ''}
                onChange={(e) => handleChange('google_search_console_id', e.target.value)}
                placeholder="google-site-verification=xxxxxx"
                data-testid="gsc-id-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Добавьте сайт на: <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="text-accent underline">search.google.com/search-console</a>
              </p>
            </div>

            <div className="p-4 bg-muted rounded">
              <p className="text-sm font-medium mb-2">Инструкции по настройке:</p>
              <ol className="text-sm space-y-1 list-decimal list-inside">
                <li>Откройте Google Search Console</li>
                <li>Добавьте ваш домен pureatelier.lv</li>
                <li>Выберите метод "HTML тег"</li>
                <li>Скопируйте content из тега meta</li>
                <li>Вставьте сюда и сохраните</li>
              </ol>
            </div>
          </div>
        </div>

        {/* SEO Meta Tags Section */}
        <div className="bg-background p-6 rounded-sm border border-border mb-6">
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp className="w-6 h-6 text-accent" />
            <h2 className="font-heading text-2xl tracking-tight">SEO Мета-теги</h2>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Заголовок (Русский)</Label>
                <Input
                  value={settings?.meta_title_ru || ''}
                  onChange={(e) => handleChange('meta_title_ru', e.target.value)}
                  data-testid="title-ru-input"
                />
              </div>
              <div>
                <Label>Заголовок (Latviešu)</Label>
                <Input
                  value={settings?.meta_title_lv || ''}
                  onChange={(e) => handleChange('meta_title_lv', e.target.value)}
                  data-testid="title-lv-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Описание (Русский)</Label>
                <Textarea
                  value={settings?.meta_description_ru || ''}
                  onChange={(e) => handleChange('meta_description_ru', e.target.value)}
                  rows={3}
                  data-testid="desc-ru-input"
                />
              </div>
              <div>
                <Label>Описание (Latviešu)</Label>
                <Textarea
                  value={settings?.meta_description_lv || ''}
                  onChange={(e) => handleChange('meta_description_lv', e.target.value)}
                  rows={3}
                  data-testid="desc-lv-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Ключевые слова (Русский)</Label>
                <Textarea
                  value={settings?.meta_keywords_ru || ''}
                  onChange={(e) => handleChange('meta_keywords_ru', e.target.value)}
                  rows={3}
                  placeholder="помолвочные кольца, кольца Рига, ювелирные изделия"
                  data-testid="keywords-ru-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Разделяйте запятыми
                </p>
              </div>
              <div>
                <Label>Ключевые слова (Latviešu)</Label>
                <Textarea
                  value={settings?.meta_keywords_lv || ''}
                  onChange={(e) => handleChange('meta_keywords_lv', e.target.value)}
                  rows={3}
                  placeholder="saderināšanās gredzeni, gredzeni Riga, juvelierizstrādājumi"
                  data-testid="keywords-lv-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Atdaliet ar komatiem
                </p>
              </div>
            </div>

            <div className="p-4 bg-muted rounded">
              <p className="text-sm font-medium mb-2">Рекомендуемые ключевые слова:</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium mb-1">Русский:</p>
                  <p className="text-muted-foreground">
                    помолвочные кольца, кольца Рига, ювелирные изделия Рига, 
                    бриллианты Рига, серьги, браслеты, эксклюзивные кольца, 
                    обручальные кольца, Pure Atelier
                  </p>
                </div>
                <div>
                  <p className="font-medium mb-1">Latviešu:</p>
                  <p className="text-muted-foreground">
                    saderināšanās gredzeni, gredzeni Riga, juvelierizstrādājumi Rīgā, 
                    dimanti Rīgā, auskari, aproces, ekskluzīvi gredzeni, 
                    laulību gredzeni, Pure Atelier
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            className="uppercase tracking-widest"
            data-testid="save-settings-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Сохранение...' : 'Сохранить настройки'}
          </Button>
        </div>

        {/* Instructions Section */}
        <div className="mt-8 bg-background p-6 rounded-sm border border-border">
          <h3 className="font-heading text-xl mb-4 tracking-tight">📋 Инструкции по подключению домена</h3>
          
          <div className="space-y-4 text-sm">
            <div>
              <p className="font-medium mb-2">1. Подключение домена pureatelier.lv:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground ml-4">
                <li>В панели Emergent нажмите "Link domain"</li>
                <li>Введите pureatelier.lv</li>
                <li>Нажмите "Entri" и следуйте инструкциям</li>
                <li>DNS изменения вступят в силу через 5-15 минут</li>
              </ol>
            </div>

            <div>
              <p className="font-medium mb-2">2. Настройка Google Analytics:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground ml-4">
                <li>Зарегистрируйтесь на analytics.google.com</li>
                <li>Создайте новый ресурс для pureatelier.lv</li>
                <li>Скопируйте Measurement ID (G-XXXXXXXXXX)</li>
                <li>Вставьте его выше и сохраните</li>
              </ol>
            </div>

            <div>
              <p className="font-medium mb-2">3. Настройка Google Search Console:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground ml-4">
                <li>Откройте search.google.com/search-console</li>
                <li>Добавьте pureatelier.lv как новый ресурс</li>
                <li>Выберите метод подтверждения "HTML тег"</li>
                <li>Скопируйте content из meta тега</li>
                <li>Вставьте его выше и сохраните</li>
                <li>Вернитесь в Search Console и нажмите "Подтвердить"</li>
              </ol>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
